import { Card } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CardTitle from './Pathway/CardTitle';
import DatePickers from './Pathway/DatePickers';
// import Progress from './Pathway/Progress';
import MainModal from './MainModal';

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value._id !== undefined && value._id !== null) return String(value._id);
    if (value.id !== undefined && value.id !== null) return String(value.id);
  }
  return String(value);
};

const getPathwayProgress = (pathway, userProjects) => {
  const projects = Array.isArray(userProjects) ? userProjects : [];
  const completedIds = new Set(
    projects
      .filter((project) => Boolean(project?.completed || project?.completedAt))
      .map((project) => normalizeId(project?.projectId))
      .filter(Boolean)
  );

  let totalProjects = 0;
  let selectedProjects = projects.length;
  let requiredProjects = 0;
  let completedRequired = 0;

  [1, 2, 3, 4, 5].forEach((levelNo) => {
    const level = pathway?.[`level${levelNo}`];
    if (!level) return;

    const mandatory = Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : [];
    const elective = Array.isArray(level?.electiveProjects) ? level.electiveProjects : [];
    const minElectives = Number(level?.minElectives || 0);
    const electiveGoal = minElectives > 0 ? minElectives : elective.length;

    totalProjects += mandatory.length + elective.length;
    requiredProjects += mandatory.length + electiveGoal;

    const mandatoryCompleted = mandatory
      .filter((project) => completedIds.has(normalizeId(project?._id))).length;
    const electiveCompleted = elective
      .filter((project) => completedIds.has(normalizeId(project?._id))).length;

    completedRequired += mandatoryCompleted + Math.min(electiveCompleted, electiveGoal);
  });

  selectedProjects = Math.min(selectedProjects, totalProjects);
  const percent = requiredProjects > 0
    ? Math.round((Math.min(completedRequired, requiredProjects) / requiredProjects) * 100)
    : 0;

  return {
    totalProjects,
    selectedProjects,
    requiredProjects,
    completedRequired,
    percent,
  };
};

export default function Pathway({ userPathway, pathway, onReload, deepLinkTarget, onDeepLinkConsumed }) {
  const { user: authUser } = useSelector((x) => x.auth);

  const [show, setShow] = useState(false);
  const [localUserPathway, setLocalUserPathway] = useState(userPathway);
  const [initialProjectId, setInitialProjectId] = useState('');
  const [initialAttemptId, setInitialAttemptId] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [reloading, setReloading] = useState(false);

  const url = process.env.REACT_APP_URL;

  useEffect(() => {
    setLocalUserPathway(userPathway);
  }, [userPathway]);

  useEffect(() => {
    if (Array.isArray(localUserPathway?.projects)) {
      setSelectedProjects(localUserPathway.projects);
    }

    const userId = normalizeId(localUserPathway?.user);
    const userPathwayId = normalizeId(localUserPathway?._id);
    const token = authUser?.token;
    if (!userId || !userPathwayId || !token) {
      setSelectedProjects([]);
      return;
    }

    let cancelled = false;

    const fetchSelectedProjects = async () => {
      try {
        const response = await fetch(`${url}/user/${userId}/userPathway/${userPathwayId}/projects`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (!cancelled) setSelectedProjects([]);
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setSelectedProjects(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setSelectedProjects([]);
      }
    };

    fetchSelectedProjects();

    return () => {
      cancelled = true;
    };
  }, [authUser?.token, localUserPathway?._id, localUserPathway?.projects, localUserPathway?.user, url]);

  const progress = useMemo(
    () => getPathwayProgress(pathway, selectedProjects),
    [pathway, selectedProjects]
  );

  useEffect(() => {
    const targetUserPathwayId = String(deepLinkTarget?.userPathwayId || '').trim();
    const localUserPathwayId = String(localUserPathway?._id || '').trim();
    const targetPathwayId = String(deepLinkTarget?.pathwayId || '').trim();
    const localPathwayId = String(localUserPathway?.pathwayId || '').trim();

    if (!deepLinkTarget) return;
    if (targetUserPathwayId && localUserPathwayId && targetUserPathwayId !== localUserPathwayId) return;
    if (!targetUserPathwayId && targetPathwayId && localPathwayId && targetPathwayId !== localPathwayId) return;

    setInitialProjectId(String(deepLinkTarget?.projectId || '').trim());
    setInitialAttemptId(String(deepLinkTarget?.attemptId || '').trim());
    setShow(true);
    onDeepLinkConsumed?.();
  }, [deepLinkTarget, localUserPathway?._id, localUserPathway?.pathwayId, onDeepLinkConsumed]);

  const handleOpen = () => setShow(true);
  const handleReload = async (event) => {
    event.stopPropagation();
    if (!onReload || reloading) return;
    setReloading(true);
    try {
      await onReload();
    } finally {
      setReloading(false);
    }
  };
  const handleClose = () => {
    setShow(false);
    setInitialProjectId('');
    setInitialAttemptId('');
  };

  return (
    <>
      <Card className="mb-3 my-pathway-card meetings-mock-card-shell meetings-page-card" style={{ cursor: 'pointer' }}>
        <button
          type="button"
          className="profile-overview-reload-btn pathway-card-reload-btn"
          onClick={handleReload}
          disabled={reloading}
          aria-label={`Reload ${pathway?.name || 'pathway'}`}
          title="Reload pathway"
        >
          <i className={`bi bi-arrow-clockwise${reloading ? ' profile-overview-reload-icon--spinning' : ''}`} aria-hidden="true" />
        </button>
        <Card.Body className="my-pathway-card-body" onClick={handleOpen}>
          <CardTitle pathway={pathway} userPathway={localUserPathway} progress={progress} />
          <div onClick={(e) => e.stopPropagation()}>
            <DatePickers
              userPathway={localUserPathway}
              onUserPathwayChange={(patch) => setLocalUserPathway((prev) => ({ ...prev, ...patch }))}
            />
          </div>
          {/* <Progress pathway={pathway} /> */}
        </Card.Body>
      </Card>
      <MainModal
        pathway={pathway}
        userPathway={localUserPathway}
        show={show}
        handleClose={handleClose}
        initialProjectId={initialProjectId}
        initialAttemptId={initialAttemptId}
      />
    </>
  );
}